import { ParticipantRole } from '../config/participants';

interface RosterRow {
  participantId?: string;
  name?: string;
  role?: string;
  email?: string;
}

export interface OwnBotParticipantInput {
  participants: readonly RosterRow[];
  /** The row the owner marked "Me" in the roster modal (the owner's only); never overridden. */
  markedParticipantId: string | null;
  /** The signed-in user's account email. */
  email?: string | null;
  displayName?: string | null;
  /** The signed-in user added this bot. Only the owner gets the fallbacks after the email match. */
  isOwner: boolean;
}

const lower = (value?: string | null): string => (value ?? '').trim().toLowerCase();

/** macOS `resolveMyIdentity`: the roster name matches the user's name, or holds their email's local part. */
const isNamedLikeUser = (rowName: string, ownName: string, emailLocal: string): boolean => {
  if (!rowName) return false;
  const matchesName = !!ownName && (rowName === ownName || rowName.includes(ownName) || ownName.includes(rowName));
  const matchesEmail = !!emailLocal && rowName.includes(emailLocal);
  return matchesName || matchesEmail;
};

/**
 * Which roster participant the signed-in user IS on a bot call. Recall ids
 * are numeric and the sockets carry a Firebase uid, so nothing on the wire says
 * which row is "me"; the alert labels ("You said") and the speech-rate banner
 * need it (2026-10-05). In order:
 *  1. the SALESPERSON row the owner marked "Me" in the roster modal (a mark
 *     on a row since re-roled as a customer no longer counts);
 *  2. the SALESPERSON row whose email (Recall's, or the calendar invite's)
 *     equals the user's account email, case-insensitively: the owner and a
 *     second salesperson watching the bot alike (coordinator rule 2026-10-05,
 *     the same on macOS and WPF);
 *  3. the owner only: the first salesperson row whose name matches their
 *     display name or email name (the macOS `resolveMyIdentity` rule), then
 *     the only salesperson on the call, so an ordinary one-rep bot call keeps
 *     showing the owner their own speed warnings (coordinator parity rule,
 *     2026-10-05). With two or more salespeople and no match: nobody.
 * Null otherwise: a watcher whose email matches no salesperson is never "You"
 * and never sees a speech-rate warning.
 */
export function resolveOwnBotParticipantId({
  participants,
  markedParticipantId,
  email,
  displayName,
  isOwner,
}: OwnBotParticipantInput): string | null {
  const salespeople = participants.filter((row) => row?.participantId && row.role === ParticipantRole.SALESPERSON);
  if (markedParticipantId && salespeople.some((row) => row.participantId === markedParticipantId)) {
    return markedParticipantId;
  }

  const ownEmail = lower(email);
  const byEmail = ownEmail ? salespeople.find((row) => lower(row.email) === ownEmail) : undefined;
  if (byEmail?.participantId) return byEmail.participantId;
  if (!isOwner) return null;

  const emailLocal = ownEmail.split('@')[0] ?? '';
  const byName = salespeople.find((row) => isNamedLikeUser(lower(row.name), lower(displayName), emailLocal));
  if (byName?.participantId) return byName.participantId;

  return salespeople.length === 1 ? (salespeople[0].participantId as string) : null;
}
